import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { Card } from '@cards/Card';
import IconButton from '@components/IconButton';
import { ProfilePictureAvatar } from '@components/ProfilePicture';
import {
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
} from '@fortawesome/sharp-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { fetchAppUsers } from '@redux/appUsers/actions';
import { selectAppUser } from '@redux/appUsers/reducers';
import {
  fetchAppUserCareTeam,
  createCareTeamRoleMember,
  createCareTeamRoleAndMember,
  editCareTeamRoleMember,
  deleteCareTeamRole,
  deleteCareTeamRoleMember,
} from '@redux/careTeam/actions';
import { selectAppUserCareTeam } from '@redux/careTeam/reducers';
import AddEditMemberModal from './AddEditMemberModal';
import './style.less';

const CareTeamMembersCard = ({ appUserId, ...props }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToDeleteMemberOnly, setMemberToDeleteMemberOnly] = useState(null);
  const [memberToDeleteBoth, setMemberToDeleteBoth] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loadingAppUser] = useSelector(selectAppUser(appUserId));
  const [loadingCareTeamRoles, careTeamRoles] = useSelector(selectAppUserCareTeam(appUserId));

  useEffect(() => {
    dispatch(fetchAppUsers());
    dispatch(fetchAppUserCareTeam(appUserId));
  }, [dispatch, appUserId]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // const hasCareTeamRoles = useMemo(() => {
  //   return careTeamRoles.length > 0;
  // }, [careTeamRoles]);

  const careTeamRoleMembers = useMemo(() => {
    if (!careTeamRoles || careTeamRoles.length === 0) {
      return [];
    }

    const entries = [];
    const seenEntryIds = new Set();

    careTeamRoles.forEach(role => {
      const members = role.members || [];

      if (members.length > 0) {
        members.forEach(member => {
          const entryId =
            member.id || `care-team-role-${role.id}-${member.staffUserId || 'member'}`;
          if (seenEntryIds.has(entryId)) {
            return;
          }
          seenEntryIds.add(entryId);

          entries.push({
            id: entryId,
            careTeamMemberId: member.id || null,
            roleId: role.id,
            roleName: role.name,
            roleDescription: role.description || '',
            name: member.name || '',
            email: member.email || '',
            phone: member.phone || '',
            businessName: member.businessName || '',
            createdByType: role.createdByType,
            staffUserId: member.staffUserId || null,
            profilePictureUrl: member.profilePictureUrl ?? null,
          });
        });
      } else {
        const entryId = `care-team-role-${role.id}`;
        if (seenEntryIds.has(entryId)) {
          return;
        }
        seenEntryIds.add(entryId);

        entries.push({
          id: entryId,
          careTeamMemberId: null,
          roleId: role.id,
          roleName: role.name,
          roleDescription: role.description || '',
          name: '',
          email: '',
          phone: '',
          businessName: '',
          createdByType: role.createdByType,
          profilePictureUrl: null,
        });
      }
    });

    return entries;
  }, [careTeamRoles]);

  const careTeamMembers = careTeamRoleMembers;

  const loading = loadingAppUser || loadingCareTeamRoles;

  const membersPerRow = windowWidth > 1366 ? 5 : windowWidth > 900 ? 4 : 3;
  const displayedMembers = isExpanded ? careTeamMembers : careTeamMembers.slice(0, membersPerRow);
  const gridClassName =
    windowWidth > 1366 ? 'five-columns' : windowWidth > 900 ? 'four-columns' : 'three-columns';

  const handleAddMember = () => {
    setSelectedMember(null);
    setModalVisible(true);
  };

  const handleEditMember = member => {
    setSelectedMember(member);
    setModalVisible(true);
  };

  const handleSaveMember = memberData => {
    const hasRoleId = !!selectedMember?.roleId;
    const hasCareTeamMemberId = !!selectedMember?.careTeamMemberId;

    if (selectedMember) {
      if (hasRoleId && hasCareTeamMemberId) {
        // Edit existing member
        dispatch(
          editCareTeamRoleMember(
            appUserId,
            selectedMember.roleId,
            selectedMember.careTeamMemberId,
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
              staffUserId: memberData.staffUserId,
              staffOrganisationId: memberData.staffOrganisationId,
            },
          ),
        );
      } else if (hasRoleId && !hasCareTeamMemberId) {
        // Add member to existing role that has no member
        dispatch(
          createCareTeamRoleMember(appUserId, selectedMember.roleId, {
            name: memberData.name,
            email: memberData.email,
            phone: memberData.phone,
            businessName: memberData.businessName,
            staffUserId: memberData.staffUserId,
            staffOrganisationId: memberData.staffOrganisationId,
          }),
        );
      }
    } else {
      // Create new role and member
      dispatch(
        createCareTeamRoleAndMember(appUserId, {
          name: memberData.roleName,
          description: memberData.roleDescription,
          members: [
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
              staffUserId: memberData.staffUserId,
              staffOrganisationId: memberData.staffOrganisationId,
            },
          ],
        }),
      );
    }
    setModalVisible(false);
    setSelectedMember(null);
  };

  const handleDeleteMember = member => {
    setModalVisible(false);
    setSelectedMember(null);
    if (member.createdByType === 'pathway_assignment') {
      setMemberToDeleteMemberOnly(member);
    } else {
      setMemberToDeleteBoth(member);
    }
  };

  const confirmDeleteMemberOnly = () => {
    if (memberToDeleteMemberOnly?.roleId && memberToDeleteMemberOnly?.careTeamMemberId) {
      dispatch(
        deleteCareTeamRoleMember(
          appUserId,
          memberToDeleteMemberOnly.roleId,
          memberToDeleteMemberOnly.careTeamMemberId,
        ),
      );
    }
    setMemberToDeleteMemberOnly(null);
  };

  const confirmDeleteBoth = () => {
    if (memberToDeleteBoth?.roleId) {
      dispatch(deleteCareTeamRole(appUserId, memberToDeleteBoth.roleId));
    }
    setMemberToDeleteBoth(null);
  };

  return (
    <Card
      className={`care-team-members-card ${gridClassName}`}
      loading={loading}
      title={t('cards:CareTeamMembers.title', { count: careTeamMembers.length })}
      extra={[
        <IconButton
          key="add-care-team-member"
          icon={faPlus}
          tooltip={t('cards:CareTeamMembers.addTooltip')}
          onClick={handleAddMember}
        />,
        ...(careTeamMembers.length > membersPerRow
          ? [
              <IconButton
                key="expand-collapse"
                icon={isExpanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter}
                tooltip={t(
                  isExpanded
                    ? 'cards:CareTeamMembers.compressTooltip'
                    : 'cards:CareTeamMembers.expandTooltip',
                )}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
              />,
            ]
          : []),
      ]}
      {...props}
    >
      <div className="care-team-members-grid">
        {displayedMembers.map(member => (
          <div
            key={member.id}
            className="care-team-member"
            onClick={() => handleEditMember(member)}
          >
            <ProfilePictureAvatar
              url={member.profilePictureUrl}
              displayName={member.name}
              size={64}
            />
            {member.name && <p className="care-team-member-name">{member.name}</p>}
            {member.roleName && <p className="care-team-member-role">{member.roleName}</p>}
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                className="care-team-member-email"
                onClick={e => e.stopPropagation()}
              >
                {member.email}
              </a>
            )}
            {member.phone && (
              <a
                href={`tel:${member.phone}`}
                className="care-team-member-phone"
                onClick={e => e.stopPropagation()}
              >
                {member.phone}
              </a>
            )}
          </div>
        ))}
      </div>
      <AddEditMemberModal
        visible={modalVisible}
        member={selectedMember}
        onSave={handleSaveMember}
        onDelete={handleDeleteMember}
        onCancel={() => {
          setModalVisible(false);
          setSelectedMember(null);
        }}
      />
      <Modal
        title={t('cards:CareTeamMembers.deleteConfirmMember.title')}
        visible={!!memberToDeleteMemberOnly}
        onOk={confirmDeleteMemberOnly}
        onCancel={() => setMemberToDeleteMemberOnly(null)}
        okText={t('cards:CareTeamMembers.deleteConfirmMember.ok')}
        cancelText={t('cards:CareTeamMembers.deleteConfirmMember.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t('cards:CareTeamMembers.deleteConfirmMember.message', {
            name: memberToDeleteMemberOnly?.name || '',
          })}
        </p>
      </Modal>
      <Modal
        title={t('cards:CareTeamMembers.deleteConfirmBoth.title')}
        visible={!!memberToDeleteBoth}
        onOk={confirmDeleteBoth}
        onCancel={() => setMemberToDeleteBoth(null)}
        okText={t('cards:CareTeamMembers.deleteConfirmBoth.ok')}
        cancelText={t('cards:CareTeamMembers.deleteConfirmBoth.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t('cards:CareTeamMembers.deleteConfirmBoth.message', {
            name: memberToDeleteBoth?.name || '',
          })}
        </p>
        <p>{t('cards:CareTeamMembers.deleteConfirmBoth.note')}</p>
      </Modal>
    </Card>
  );
};

CareTeamMembersCard.propTypes = {
  appUserId: string.isRequired,
};

export default CareTeamMembersCard;
